<script setup lang="ts">
/**
 * Portal App Root Component
 */
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useAuthStore, useUIStore, MermaidZoom, ToastBridge } from '@claimshield/shared'
import DemoTour from './components/demo/DemoTour.vue'
import { DEMO_TOUR_STEPS } from './components/demo/demoTourSteps'
import { launchTourStepsFor } from './components/tour/launchTourSteps'
import { LAUNCH_TOUR_EVENT, useLaunchTour } from './components/tour/useLaunchTour'
import RouteLoadingBoundary from './components/RouteLoadingBoundary.vue'

const authStore = useAuthStore()
const uiStore = useUIStore()

// Demo-mode guided tour: the staff demo banner (in @claimshield/shared's
// PortalLayout) fires a `cs:demo-tour-start` window event; the portal app root
// owns the tour mount so shared stays decoupled from this portal-only component.
const tourActive = ref(false)
const onTourStart = () => {
  tourActive.value = true
}

// The tour only makes sense inside demo mode — ending the demo (toggle,
// client switch, logout) ends the tour with it, so the tour card can't
// linger over a non-demo portal where actions are no longer simulated.
watch(
  () => authStore.demoMode,
  (on) => {
    if (!on) tourActive.value = false
  },
)

// ── First-login tour (client-support Phase 3A) ───────────────────────────────
// Same engine, its own step list, and its own trigger. Gated on the REAL token's
// role rather than `isClient` (which reads through view-as): a staff member
// looking at a carrier's portal is not that carrier's first login, and a demo is
// a sales walkthrough that already has its own tour.
const {
  active: launchTourActive,
  maybeAutoStart,
  cancelPending,
  start: startLaunchTour,
} = useLaunchTour()
const isRealClient = computed(() => authStore.user?.role === 'client')
const onLaunchTourStart = () => {
  startLaunchTour()
}
const onboardingPanelVisible = ref(false)
const launchTourSteps = computed(() => launchTourStepsFor(onboardingPanelVisible.value))
const onOnboardingVisibility = (event: Event) => {
  onboardingPanelVisible.value = (event as CustomEvent<boolean>).detail
}

// Only one tour can be on screen at a time. They are mutually exclusive by
// construction (demo mode is staff-only, the launch tour is client-only), but
// staff can reach a client portal in view-as, so the guard is written down
// rather than assumed.
watch(tourActive, (on) => {
  if (on) launchTourActive.value = false
})
watch(launchTourActive, (on) => {
  if (on) tourActive.value = false
})

// The app also mounts on /login. A one-time onMounted check is spent before a
// client signs in, so follow the real authenticated identity instead. The
// composable waits for the dashboard and drops a pending start on session change.
watch(
  () => [authStore.user?.id, isRealClient.value, authStore.isAuthenticated, authStore.demoMode] as const,
  ([userId, client, authenticated, demo], _previous, onCleanup) => {
    cancelPending()
    if (client && authenticated && !demo && userId) {
      void maybeAutoStart(userId)
      onCleanup(cancelPending)
    }
  },
  { immediate: true },
)

onMounted(async () => {
  // Initialize UI (theme, sidebar state)
  uiStore.initialize()
  // Initialize auth
  await authStore.initialize()
  window.addEventListener('cs:demo-tour-start', onTourStart)
  window.addEventListener(LAUNCH_TOUR_EVENT, onLaunchTourStart)
  window.addEventListener('cs:onboarding-panel-visibility', onOnboardingVisibility)
})

onBeforeUnmount(() => {
  window.removeEventListener('cs:demo-tour-start', onTourStart)
  window.removeEventListener(LAUNCH_TOUR_EVENT, onLaunchTourStart)
  window.removeEventListener('cs:onboarding-panel-visibility', onOnboardingVisibility)
})
</script>

<template>
  <PToast position="top-right" :baseZIndex="9999" />
  <ToastBridge />
  <MermaidZoom />
  <RouteLoadingBoundary />
  <DemoTour v-model:active="tourActive" :steps="DEMO_TOUR_STEPS" />
  <!-- Live account, no demo banner on screen: park selector-less cards under the
       portal header instead (both headers carry the anchor; the engine takes the
       visible one). -->
  <DemoTour
    v-model:active="launchTourActive"
    :steps="launchTourSteps"
    chrome-selector="[data-tour-chrome]"
  />
</template>
